import { useEffect, useRef, useState } from "react";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import CTLTable from "application/components/CTLTable";
import { getColumns, Attributes, operationURLS } from "./model";
import { application } from "globals/endpoints";
import CTLBaseDialogForm from "application/components/CTLBaseDialogForm";
import DeleteConfirmation from "application/components/DeleteConfirmation";
import CTLNotification from "application/components/CTLNotification";
import WorkingHolidayType from "./workingHolidayType";
import { getYearlyColumns } from "./yearlyModel";
import { getUpdateColumns } from "./updateModel";
import WarningIcon from "@mui/icons-material/DeleteOutline";
import { red } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Icon,
  CircularProgress,
  Box,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CTLAdvancedFilter from "application/components/CTLAdvancedFilter";

export default function nonWorkingHoliday({
  onLoad,
  pageId,
  searchText,
  registerCallBacks,
  pageName,
}) {
  const [controller, dispatch] = useMaterialUIController();
  const {
    activePage,
    darkMode,
    tableSortColumn,
    tableColumnDataType,
    tableColumnDirection,
  } = controller;
  const [loading, setLoading] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [openUpdateForm, setOpenUpdateForm] = useState(false);
  const [openAddForm, setOpenAddForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [updateList, setUpdateList] = useState(Date.now());
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const nonWorkingHolidayType = useRef(null);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [deleteWeekly, setDeleteWeekly] = useState(false);
  const [deleteYearly, setDeleteYearly] = useState(false);

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  const [openWarning, setOpenWarning] = useState(false);
  const [openDeleteWarning, setOpenDeleteWarning] = useState(false);
  const [pendingData, setPendingData] = useState(null);

  useEffect(() => {
    Attributes && onLoad(Attributes);
    registerCallBacks &&
      registerCallBacks({
        openAdvancedFilter: onOpenFilter,
        openForm: onOpenForm,
        DownloadPdf: DownloadPdf,
        onMultipleDelete: onMultipleDelete,
      });
  }, [
    activePage,
    advancedFilters,
    tableSortColumn,
    tableColumnDataType,
    tableColumnDirection,
  ]);

  function onOpenFilter() {
    setOpenFilter(true);
  }

  function onCloseFilter() {
    setOpenFilter(false);
  }

  function onApplyFilter(advancedFilters) {
    setAdvancedFilters(advancedFilters);
    setOpenFilter(false);
  }

  const handleDeleteWarningConfirm = () => {
    application
      .post(operationURLS.deleteAll, {
        isWeekly: deleteWeekly,
        isYearly: deleteYearly,
      })
      .then((response) => {
        setUpdateList(Date.now());
        setOpenDeleteWarning(false);
        setDeleteWeekly(false);
        setDeleteYearly(false);
        setNotify({
          isOpen: true,
          type: "success",
          pagename: pageName,
          status: response.data.messageDetail,
        });
      })
      .catch((error) => {
        setOpenDeleteWarning(false);
        setDeleteWeekly(false);
        setDeleteYearly(false);
        setNotify({
          isOpen: true,
          message: error.response.data.message,
          type: "error",
          pagename: pageName,
          status: error.response.data.messageDetail,
        });
      });
  };

  const handleDeleteWarningCancel = () => {
    setOpenDeleteWarning(false);
    setDeleteWeekly(false);
    setDeleteYearly(false);
  };

  const onMultipleDelete = () => {
    setOpenDeleteWarning(true);
  };

  const onSave = (data) => {
    nonWorkingHolidayType.current = data;
    setFormData({
      eventTime: Date.now(),
    });
    setOpenForm(true);
  };

  function onOpenForm() {
    setOpenDialog(true);
  }

  const DownloadPdf = () => {
    setLoading(true);
    const requestBodyData = {
      sortDirection: tableColumnDirection || "desc",
      sortBy: tableSortColumn || "date",
      search: searchText,
      sortDataType: tableColumnDataType || "string",
      columnNames: [
        "day",
        "date",
        "holidayName",
        "fromTime",
        "toTime",
        "servingApp",
        "servingData",
      ],
      advancedFilters: advancedFilters,
    };
    const requestConfig = {
      responseType: "arraybuffer",
      params: {
        reportType: "NonWorkingHolidayPdf",
      },
    };
    application
      .post(operationURLS.pdfDownload, requestBodyData, requestConfig)
      .then((response) => {
        if (response) {
          setLoading(false);
          const blob = new Blob([response.data], { type: "application/pdf" });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "Non-Working-Holiday.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);

          setNotify({
            isOpen: true,
            pagename: pageName,
            status: "PDF Download successful",
            type: "success",
          });
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) {
          setNotify({
            isOpen: true,
            pagename: pageName,
            status: "Unable To Download File",
            type: "error",
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const editRow = (row) => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const index = row.day === 0 ? -1 : row.day - 1;
    const dayName = days[index] || "";

    setFormData({ ...row, day: dayName });
    setOpenUpdateForm(true);
  };

  function onCreate(row) {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const index = row.day === 0 ? -1 : row.day - 1;
    const dayName = days[index] || "";

    setFormData({ ...row, day: dayName });
    setOpenAddForm(true);
  }

  const deleteRow = (row) => {
    setSelectedRow(row);
    setConfirmDelete(true);
  };

  const onCancel = () => {
    setSelectedRow([]);
    setConfirmDelete(false);
  };

  const onAgree = (row) => {
    application
      .post(operationURLS.delete, {
        intId: [row.id],
      })
      .then((response) => {
        setUpdateList(Date.now());
        setNotify({
          isOpen: true,
          type: "success",
          pagename: pageName,
          status: response.data.messageDetail,
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: error.response.data.message,
          type: "error",
          pagename: pageName,
          status: error.response.data.messageDetail,
        });
      });

    setConfirmDelete(false);
  };

  function onSaveForm(data) {
    if (nonWorkingHolidayType.current === "Weekly") {
      setPendingData(data);
      setOpenWarning(true);
      return;
    }else if(nonWorkingHolidayType.current === "Yearly"){
      const selectedDate = new Date(data.date);
      const selectedDayStart = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      ).getTime();

      const today = new Date();
      const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      ).getTime();

      if (selectedDayStart < todayStart) {
        setNotify({
          isOpen: true,
          type: "warning",
          pagename: pageName,
          status: "Selected date cannot be in the past.",
        });
        return;
      }
      handleSaveAPICall(data);
    }
  }

  const handleSaveAPICall = (data) => {
    const dayMap = {
      sunday: 1,
      monday: 2,
      tuesday: 3,
      wednesday: 4,
      thursday: 5,
      friday: 6,
      saturday: 7,
    };
    const numberArray = Object.keys(dayMap)
      .filter((day) => data[day] === true)
      .map((day) => dayMap[day]);

    if (nonWorkingHolidayType.current === "Weekly") {
      const payload = {
        ...data,
        number: numberArray,
      };
      application
        .post(operationURLS.create, payload)
        .then((response) => {
          setUpdateList(Date.now());
          onCloseForm();
          setNotify({
            isOpen: true,
            type: "success",
            pagename: pageName,
            status: response.data.message,
          });
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            type: "error",
            pagename: pageName,
            status: error.response.data.message,
          });
        });
    } else if (nonWorkingHolidayType.current === "Yearly") {
      const payload = { ...data, date: new Date(data.date).getTime() };
      application
        .post(operationURLS.yearlyCreate, payload)
        .then((response) => {
          setUpdateList(Date.now());
          onCloseForm();
          setNotify({
            isOpen: true,
            type: "success",
            pagename: pageName,
            status: response.data.message,
          });
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            type: "error",
            message: "Failed to create Data!",
            pagename: pageName,
            status: error.response.data.message,
          });
        });
    } else {
      setNotify({
        isOpen: true,
        type: "warning",
        message: "",
        pagename: pageName,
        status: "Please Select Non Working Holiday Type",
      });
    }
  };

  const handleWarningConfirm = () => {
    setOpenWarning(false);
    if (pendingData) handleSaveAPICall(pendingData);
    setPendingData(null);
  };

  const handleWarningCancel = () => {
    setOpenWarning(false);
    setPendingData(null);
  };

  function onUpdateSave(data) {
    const dayMap = {
      sunday: 1,
      monday: 2,
      tuesday: 3,
      wednesday: 4,
      thursday: 5,
      friday: 6,
      saturday: 7,
    };
    const dayInt = dayMap[data.day.toLowerCase()] || null;
    const payload = {
      id: data.id,
      fromTime: data.fromTime,
      toTime: data.toTime,
      servingApp: data.servingApp,
      servingData: data.servingData,
      number: [dayInt],
    };
    if (data.id) {
      application
        .post(operationURLS.update, payload)
        .then((response) => {
          setUpdateList(Date.now());
          onCloseForm();
          setNotify({
            isOpen: true,
            type: "success",
            pagename: pageName,
            status: response.data.messageDetail,
            message: response.data.message,
          });
        })
        .catch((error) => {
          setNotify({
            isOpen: true,
            type: "error",
            message: error.response.data.messageDetail,
            pagename: pageName,
            status: error.response.data.message,
          });
        });
    } else {
      setNotify({
        isOpen: true,
        type: "error",
        pagename: pageName,
        status: "Id Not Found!",
      });
    }
  }

  function onAddSave(data) {
    const dayMap = {
      sunday: 1,
      monday: 2,
      tuesday: 3,
      wednesday: 4,
      thursday: 5,
      friday: 6,
      saturday: 7,
    };
    const dayInt = dayMap[data.day.toLowerCase()] || null;

    const payload = {
      id: data.id,
      number: [dayInt] || [0],
      fromTime: data.fromTime,
      toTime: data.toTime,
      servingApp: data.servingApp,
      servingData: data.servingData,
    };
    application
      .post(operationURLS.crateSingle, payload)
      .then((response) => {
        setUpdateList(Date.now());
        onCloseForm();
        setNotify({
          isOpen: true,
          type: "success",
          pagename: pageName,
          status: response.data.message,
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          type: "error",
          pagename: pageName,
          status: error.response.data.message,
        });
      });
  }

  const onDeactivate = (row) => {
    application
      .post(operationURLS.isActive, {
        id: row.id,
        isActive: row.isActive == true ? false : true,
      })
      .then((response) => {
        setUpdateList(Date.now());
        setNotify({
          isOpen: true,
          type: "success",
          pagename: pageName,
          status: response.data.messageDetail,
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: error.response.data.message,
          type: "error",
          pagename: pageName,
          status: error.response.data.messageDetail,
        });
      });
  };

  function onCloseForm() {
    setOpenForm(false);
    setOpenAddForm(false);
    setOpenUpdateForm(false);
    setFormData("");
    nonWorkingHolidayType.current = null;
  }

  return (
    <MDBox>
      <WorkingHolidayType
        onSave={onSave}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
      />

      <DeleteConfirmation
        entityName={pageName}
        data={selectedRow}
        columns={getColumns()}
        confirmDelete={confirmDelete}
        onAgree={onAgree}
        onCancel={onCancel}
      />

      {/* update form */}
      <CTLBaseDialogForm
        entityName={`${pageName}`}
        columns={getUpdateColumns(formData)}
        openForm={openUpdateForm}
        onSaveForm={onUpdateSave}
        onCloseForm={onCloseForm}
        formData={formData}
        pageAttributes={Attributes}
      />

      {/* add form */}
      <CTLBaseDialogForm
        entityName={`${pageName}`}
        columns={getUpdateColumns(formData)}
        openForm={openAddForm}
        onSaveForm={onAddSave}
        onCloseForm={onCloseForm}
        formData={formData}
        pageAttributes={Attributes}
      />

      {/* create form weekly/yearly */}
      <CTLBaseDialogForm
        entityName={`${pageName} ${nonWorkingHolidayType.current} `}
        columns={
          nonWorkingHolidayType.current === "Weekly"
            ? getColumns(deleteRow, editRow)
            : getYearlyColumns()
        }
        openForm={openForm}
        onSaveForm={onSaveForm}
        onCloseForm={onCloseForm}
        formData={formData}
        pageAttributes={Attributes}
      />

      <CTLTable
        apiURL={operationURLS.list}
        advancedFilters={advancedFilters}
        columns={getColumns(deleteRow, editRow, onCreate, onDeactivate)}
        entriesPerPage={false}
        canSearch
        defaultSortDirection="desc"
        defaultSortColumn="date"
        pageId={pageId}
        list
        searchText={searchText}
        hasRowSelection={false}
        updateList={updateList}
      />

      <CTLAdvancedFilter
        entityName={pageName}
        columns={getColumns()}
        applyFilter={openFilter}
        onApplyFilter={onApplyFilter}
        onCloseFilter={onCloseFilter}
        advancedFilters={advancedFilters}
      />

      <MDBox>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100px",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </MDBox>
      <CTLNotification notify={notify} setNotify={setNotify} />

      <Dialog open={openWarning} onClose={handleWarningCancel}>
        <DialogTitle sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <Icon color="warning">warning</Icon> Warning
        </DialogTitle>
        <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <MDTypography>
            After saving, your previous weekly data if exists, it will be
            updated. Do you want to continue?
          </MDTypography>
        </DialogContent>
        <DialogActions sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <MDButton onClick={handleWarningCancel} color="secondary">
            Cancel
          </MDButton>
          <MDButton
            onClick={handleWarningConfirm}
            color="error"
            variant="contained"
          >
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteWarning}
        onClose={() => handleDeleteWarningCancel()}
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: darkMode
              ? theme.palette.background.default
              : theme.palette.white.main,

            px: 3,
            py: 3,
            width: { xs: 370, md: 460 },
            boxShadow: theme.shadows[5],
            border: darkMode ? "1px solid #2c2c3e" : "1px solid #d1d5db",
          },
        }}
      >
        <Box display="flex" justifyContent="center" mb={2}>
          <Box
            sx={{
              p: 2,
              bgcolor: red[100],
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <WarningIcon sx={{ color: red[600], fontSize: 28 }} />
          </Box>
        </Box>

        <DialogTitle sx={{ p: 0, textAlign: "center" }}>
          {
            <Typography variant="h6" fontWeight={600} color="text">
              Are you sure?
            </Typography>
          }
        </DialogTitle>

        <DialogContent
          sx={{
            mt: 1,
            p: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            color="error"
            sx={{ textAlign: "center", mt: 1, mb: 1 }}
          >
            You want to delete{" "}
            <span style={{ color: "#d32f2f" }}>all records</span> on non-working
            holidays?
          </Typography>
          <MDBox
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteWeekly}
                  onChange={(e) => setDeleteWeekly(e.target.checked)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label="Weekly"
              sx={{ mb: 0.5 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteYearly}
                  onChange={(e) => setDeleteYearly(e.target.checked)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label="Yearly"
            />
          </MDBox>
        </DialogContent>

        <DialogActions
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "center",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
            px: 0,
          }}
        >
          <MDButton
            onClick={() => handleDeleteWarningCancel()}
            variant="outlined"
            color="text"
            sx={{
              width: { xs: "100%", md: 140 },
              height: 40,
              fontWeight: 500,
              fontSize: 14,
              textTransform: "none",
              borderColor: "#d1d5db",
              "&:hover": {
                backgroundColor: "#f3f4f6",
                color: "#111827",
                borderColor: "#d1d5db",
              },
            }}
          >
            Cancel
          </MDButton>

          <MDButton
            onClick={() => handleDeleteWarningConfirm()}
            variant="outlined"
            color="error"
            disabled={!deleteWeekly && !deleteYearly}
            sx={{
              width: { xs: "100%", md: 140 },
              height: 40,
              fontWeight: 500,
              fontSize: 14,

              textTransform: "none",
              "&:hover": {
                backgroundColor: red[700],
                color: "#fff",
                borderColor: red[700],
              },
            }}
          >
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
